// Libs
import axios, { AxiosRequestConfig } from 'axios';
import { useState } from 'react';
import merge from 'lodash/merge';
// App
import { AxiosHookResponse } from './axios-request.hooks.interfaces';

export * from './axios-request.hooks.interfaces';

/**
 * Hook for axios get requests
 */
export const useAxiosGetRequest = <ResponseData = unknown>(
  url: string,
  initialValue?: ResponseData,
  config?: AxiosRequestConfig
): AxiosHookResponse<void, ResponseData> => {
  const getRequestDefaultConfig: AxiosRequestConfig = {
    method: 'get',
    url,
  };

  return useAxiosRequest(initialValue, merge(getRequestDefaultConfig, config));
};

/**
 * Hook for axios post requests
 * Post requests are not handled by SWR because it does not provide the same advantages it can provide for get requests (mainly caching)
 */
export const useAxiosPostRequest = <BodyParam = unknown, ResponseData = unknown>(
  url: string,
  initialValue?: ResponseData,
  config?: AxiosRequestConfig
): AxiosHookResponse<BodyParam, ResponseData> => {
  const postRequestDefaultConfig: AxiosRequestConfig = {
    method: 'post',
    url,
  };

  return useAxiosRequest(initialValue, merge(postRequestDefaultConfig, config));
};

/**
 * Hook for axios put requests
 * Put requests are not handled by SWR because it does not provide the same advantages it can provide for get requests (mainly caching)
 */
export const useAxiosPutRequest = <BodyParam = unknown, ResponseData = unknown>(
  url: string,
  initialValue?: ResponseData,
  config?: AxiosRequestConfig
): AxiosHookResponse<BodyParam, ResponseData> => {
  const putRequestDefaultConfig: AxiosRequestConfig = {
    method: 'put',
    url,
  };

  return useAxiosRequest(initialValue, merge(putRequestDefaultConfig, config));
};

/**
 * Hook for axios generic requests
 */
const useAxiosRequest = <BodyParam = unknown, ResponseData = unknown>(
  initialValue?: ResponseData,
  config?: AxiosRequestConfig
): AxiosHookResponse<BodyParam, ResponseData> => {
  const isExecuteReady = config.url != null;
  const [isLoading, setIsLoading] = useState(null);
  const [error, setError] = useState(null);
  const [data, setData] = useState(initialValue);

  /**
   * Execute request
   * @param bodyParam the body params for request
   * @param queryParams the query params for request
   */
  const execute = async (bodyParam?: BodyParam, queryParams?: unknown) => {
    if (!isExecuteReady || isLoading) return;

    setIsLoading(true);
    setError(null);

    if (bodyParam) {
      config.data = bodyParam;
    }
    if (queryParams) {
      config.params = merge(config.params, queryParams);
    }

    const response = await axios.request(config).catch((error) => setError(error));

    if (response && response.data) {
      setData(response.data);
    }

    setIsLoading(false);

    return response;
  };

  return {
    data,
    setData,
    isLoading,
    error,
    execute,
    isExecuteReady,
  };
};
